import { DyRecommendationQueryCondition, DyRecommendationRule } from '@lib/dy';
import { FacetFilter, NumericFilter, Refinements } from '@packages/common';

const dyFieldNames = [
  'feature_solidWood',
  'feature_surface',
  'feature_materialTables',
  'feature_material',
  'feature_materialCouchTable',
  'feature_sale',
  'feature_materialGardenFurniture',
  'feature_typeOfWood',
  'feature_coverMaterialChairs',
  'feature_style',
  'feature_shop',
  'feature_materialFurniture',
  'feature_lyingDimensions',
  'feature_materialBeds',
  'feature_materialLamps',
  'feature_coverMaterial',
];

// Unlike other facets, `colour` and `brand` doesnt have `feature_` prefix on DY side
const remappedFilterNames = {
  'features.colour': 'colour',
  'features.brand': 'brand',
};

function getFieldName(filterName: string): string | null {
  // in the dy data-feed theses fields are named 'feature' (singular) and
  // separated by underscores instead of dots
  const dyFilterName = filterName.replace('.', '_').replace('features', 'feature');
  if (dyFieldNames.includes(dyFilterName)) {
    return dyFilterName;
  }

  return remappedFilterNames[filterName] || null;
}

function transformNumericFilter(filterName: string, numericFilter: NumericFilter): DyRecommendationQueryCondition[] {
  const field = getFieldName(filterName);
  if (!field) {
    return [];
  }
  const { min, max } = numericFilter.value;

  return [
    {
      field,
      arguments: [
        {
          action: 'GTE',
          value: min,
        },
      ],
    },
    {
      field,
      arguments: [
        {
          action: 'LTE',
          value: max,
        },
      ],
    },
  ];
}

function transformFacetFilter(filterName: string, facetFilter: FacetFilter): DyRecommendationQueryCondition {
  // Due to special character restrictions on dy side, facet names store with `_` in between instead of `.`
  const field = getFieldName(filterName);
  if (!field) {
    return {
      field: 'ignore',
      arguments: [],
    };
  }

  return {
    field,
    arguments: facetFilter.values.map((value) => ({
      action: 'CONTAINS',
      // "true" is set as 1 in dy feed, thats why if value is true we convert it to 1
      value: value.featureValue === 'true' ? 1 : value.featureValue,
    })),
  };
}

function transformCategoryTitleToQueryCondition(categoryTitle?: string): DyRecommendationQueryCondition {
  if (!categoryTitle) {
    return {} as DyRecommendationQueryCondition;
  }
  return {
    field: 'keywords',
    arguments: [
      {
        action: 'CONTAINS',
        // This should match with the field team discovery uses for the dy feed.
        value: categoryTitle,
      },
    ],
  };
}

export function createDyRecommendationRules(categoryTitle: string, refinements?: Refinements): DyRecommendationRule[] {
  const { facetFilters, numericFilters } = refinements ?? {
    facetFilters: {},
    numericFilters: {},
  };
  const transformedFacetFilters = Object.entries(facetFilters)
    .map(([filterName, facetFilter]) => transformFacetFilter(filterName, facetFilter))
    .filter((item) => item.field !== 'ignore');

  const transformedNumericFilters = Object.entries(numericFilters).flatMap(([filterName, numericFilter]) =>
    transformNumericFilter(filterName, numericFilter),
  );

  const transformedCategoryFilter = transformCategoryTitleToQueryCondition(categoryTitle);
  const conditions: DyRecommendationQueryCondition[] = [
    ...transformedFacetFilters,
    ...transformedNumericFilters,
    transformedCategoryFilter,
  ]
    .filter((condition) => !!condition)
    .filter((condition) => Object.keys(condition).length > 0);

  return conditions.length > 0
    ? [
        {
          id: '-1',
          query: {
            conditions,
          },
          slots: [],
          type: 'include',
        },
      ]
    : [];
}
